import React, { useState, useEffect, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import swal from "sweetalert";
import {
  FaArrowLeft,
  FaPrint,
  FaUndoAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaReceipt,
  FaCheckCircle,
  FaBoxOpen,
  FaShieldAlt,
} from "react-icons/fa";

export default function SaleOrderDetailsView(props) {
  const history = useHistory();
  const routeParams = useParams();
  const orderId = props.match?.params?.order_id || routeParams?.order_id;

  const [loading, setLoading] = useState(true);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [allRefundCheck, setAllRefundCheck] = useState(0);
  const [remainingRefundable, setRemainingRefundable] = useState(0);
  const [totalRefunded, setTotalRefunded] = useState(0);
  const [order, setOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const getValidMoment = (date1, date2) => {
    if (date1 && typeof date1 === "string" && !date1.startsWith("0000") && moment(date1).isValid()) return moment(date1);
    if (date2 && typeof date2 === "string" && !date2.startsWith("0000") && moment(date2).isValid()) return moment(date2);
    return null;
  };

  const fetchInvoiceDetails = useCallback(() => {
    setLoading(true);
    axios.get(`/api/fetch-invoice-details/${orderId}`).then((res) => {
      if (res.data.status === 200) {
        setInvoiceDetails(res.data.data.orderDetails || []);
        setTotalCost(res.data.data.total_products_cost || 0);
        setAllRefundCheck(res.data.data.refund_all_check || 0);
        setRemainingRefundable(res.data.data.remaining_refundable ?? 0);
        setTotalRefunded(res.data.data.total_refunded ?? 0);
        const orderData = Array.isArray(res.data.data.order)
          ? res.data.data.order[0]
          : res.data.data.order;
        setOrder(orderData);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    document.title = `Sales Invoice #${orderId} — Admin`;
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails, orderId]);

  const handleRefundAll = () => {
    const refundableAmt = remainingRefundable > 0 ? remainingRefundable : totalCost;
    swal({
      title: "Process Full Order Refund?",
      text: `Are you sure you want to refund Rs. ${parseFloat(refundableAmt).toLocaleString()} to the customer? This will post a balanced accounting reversal and restock items back into warehouse inventory.`,
      icon: "warning",
      buttons: ["Cancel", "Confirm Full Refund"],
      dangerMode: true,
    }).then((willRefund) => {
      if (willRefund) {
        setActionLoading(true);
        const payload = {
          orderId: orderId,
          type: "C",
          restock_inventory: true,
        };

        axios.post(`/api/refund-all-order`, payload).then((res) => {
          setActionLoading(false);
          if (res.data.status === 200) {
            swal("Refund Processed", res.data.message || "Order refund has been completed and inventory restocked.", "success");
            fetchInvoiceDetails();
          } else {
            swal("Notice", res.data.message || "Failed to process refund", "error");
            fetchInvoiceDetails();
          }
        }).catch((err) => {
          setActionLoading(false);
          swal("Error", err.response?.data?.message || "Server error while processing refund", "error");
          fetchInvoiceDetails();
        });
      }
    });
  };

  const handleRefundSingleItem = (productId, productName, variantId) => {
    swal({
      title: `Refund ${productName}?`,
      text: "This item will be refunded to the customer and automatically restocked into warehouse inventory.",
      icon: "warning",
      buttons: ["Cancel", "Confirm Item Refund & Restock"],
      dangerMode: true,
    }).then((willRefund) => {
      if (willRefund) {
        setActionLoading(true);
        const payload = {
          orderId: orderId,
          product_id: productId,
          variant_id: variantId || undefined,
          type: "C",
          refund_by_product: "RBP",
          restock_inventory: true,
        };

        axios.post(`/api/refund-order-product`, payload).then((res) => {
          setActionLoading(false);
          if (res.data.status === 200) {
            swal("Item Refunded", res.data.message || "Product item refunded and inventory restocked.", "success");
            fetchInvoiceDetails();
          } else {
            swal("Notice", res.data.message || "Failed to process item refund", "error");
            fetchInvoiceDetails();
          }
        }).catch((err) => {
          setActionLoading(false);
          swal("Error", err.response?.data?.message || "Server error processing product refund", "error");
          fetchInvoiceDetails();
        });
      }
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 small">Loading sales invoice details...</p>
      </div>
    );
  }

  const customerName = order
    ? `${order.firstname || ""} ${order.lastname || ""}`.trim() || "Customer"
    : "Customer";

  const isFullyRefunded =
    Number(allRefundCheck) === 1 ||
    order?.order_status === "Refunded" ||
    Number(order?.status) === 5 ||
    (Number(totalCost) > 0 && Number(remainingRefundable) <= 0.01 && Number(totalRefunded) > 0);

  return (
    <div className="container-fluid px-0">
      {/* Top Breadcrumb and Actions */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/refund/sale-orders" className="text-decoration-none text-muted">
                  Sales Orders
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Invoice #{order?.tracking_no || orderId}
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Sales Order Invoice Details
            </h3>
            {isFullyRefunded ? (
              <span className="admin-badge admin-badge-danger">
                <FaUndoAlt size={10} /> Fully Refunded
              </span>
            ) : totalRefunded > 0 ? (
              <span className="admin-badge admin-badge-warning">
                <FaUndoAlt size={10} /> Partially Refunded
              </span>
            ) : (
              <span className="admin-badge admin-badge-success">
                <FaCheckCircle size={10} /> Commercial Sale Completed
              </span>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {!isFullyRefunded && (remainingRefundable > 0 || Number(totalCost) > 0) && (
            <button
              onClick={handleRefundAll}
              disabled={actionLoading}
              className="btn btn-danger d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm rounded-3"
            >
              {actionLoading ? <span className="spinner-border spinner-border-sm me-1" /> : <FaUndoAlt />} Refund Entire Order
            </button>
          )}
          <button
            onClick={() => window.print()}
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaPrint /> Print Invoice
          </button>
          <Link
            to="/admin/refund/sale-orders"
            className="btn btn-outline-dark d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaArrowLeft /> Back to Sales
          </Link>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column: Line Items & Actions */}
        <div className="col-lg-8">
          {/* Items Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white d-flex align-items-center justify-content-between">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaBoxOpen className="text-primary" /> Purchased Line Items ({invoiceDetails.length})
              </h5>
              <span className="text-muted small">Manage item-level returns & refunds</span>
            </div>

            <div className="table-responsive">
              <table className="admin-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Product Item</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Discount</th>
                    <th className="text-end">Total Price</th>
                    <th className="text-end" style={{ width: "130px" }}>Item Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceDetails.map((item, index) => {
                    const unitPrice = parseFloat(item.product_price || 0);
                    const qty = parseInt(item.product_qty || 1);
                    const lineTotal = parseFloat(item.total_price || unitPrice * qty);
                    const discount = parseFloat(item.discount_given || 0);
                    const isItemRefunded = Number(item.refund) === 1 || Number(item.item_refunded) === 1 || isFullyRefunded;

                    return (
                      <tr key={index}>
                        <td className="text-muted fw-semibold">{index + 1}</td>
                        <td>
                          <div className="fw-bold text-dark">{item.product_name}</div>
                          <span className="text-muted small">
                            {item.sku ? `Item code: ${item.sku}` : `Product ID: #${item.product_id || "—"}`}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-light text-dark border px-2 py-1">
                            {qty}x
                          </span>
                        </td>
                        <td className="text-end text-muted font-monospace">
                          Rs. {unitPrice.toLocaleString()}
                        </td>
                        <td className="text-end text-danger font-monospace">
                          {discount > 0 ? `- Rs. ${discount.toLocaleString()}` : "—"}
                        </td>
                        <td className="text-end fw-bold text-dark font-monospace">
                          Rs. {lineTotal.toLocaleString()}
                        </td>
                        <td className="text-end">
                          {isItemRefunded ? (
                            <span className="badge bg-secondary bg-opacity-10 text-secondary px-2 py-1 fw-semibold">
                              Refunded
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRefundSingleItem(item.product_id, item.product_name, item.variant_id)}
                              disabled={actionLoading}
                              className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 rounded-3 px-2 py-1 fw-semibold"
                            >
                              {actionLoading ? <span className="spinner-border spinner-border-sm" /> : <FaUndoAlt size={10} />} Refund Item
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Financial Breakdown Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaReceipt className="text-success" /> Commercial Financial Snapshot
              </h5>
            </div>
            <div className="admin-card-body">
              {(() => {
                const originalItemsSubtotal = invoiceDetails.reduce(
                  (sum, item) => sum + parseFloat(item.product_price || 0) * parseInt(item.product_qty || 1),
                  0
                );
                const totalCatalogDiscount = invoiceDetails.reduce(
                  (sum, item) => sum + parseFloat(item.discount_given || 0),
                  0
                );
                const couponDisc = parseFloat(order?.coupon_discount || 0);
                const totalSavings = totalCatalogDiscount + couponDisc;
                const paidSubtotal = parseFloat(totalCost || (originalItemsSubtotal - totalCatalogDiscount));

                return (
                  <>
                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-muted">Items Subtotal (Real Price)</span>
                      <span className="fw-semibold text-dark font-monospace">
                        Rs. {originalItemsSubtotal.toLocaleString()}
                      </span>
                    </div>

                    {totalCatalogDiscount > 0 && (
                      <div className="d-flex justify-content-between py-2 border-bottom text-success">
                        <span>Catalog Discount Savings</span>
                        <span className="fw-bold font-monospace">
                          - Rs. {totalCatalogDiscount.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {totalCatalogDiscount > 0 && (
                      <div className="d-flex justify-content-between py-2 border-bottom bg-light px-2 rounded">
                        <span className="fw-semibold text-dark">Items Subtotal (Discounted)</span>
                        <span className="fw-bold text-dark font-monospace">
                          Rs. {paidSubtotal.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {couponDisc > 0 && (
                      <div className="d-flex justify-content-between py-2 border-bottom text-danger">
                        <span>Coupon Discount {order?.coupon_code ? `(${order.coupon_code})` : ""}</span>
                        <span className="fw-bold font-monospace">
                          - Rs. {couponDisc.toLocaleString()}
                        </span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-muted">Payment Provider Settlement</span>
                      <span className="fw-semibold text-success font-monospace">
                        {order?.payment_mode || "COD / Cash on Delivery"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-muted">Revenue Recognition Status</span>
                      <span className="badge bg-success bg-opacity-10 text-success fw-semibold px-2 py-1">
                        Recognized in General Ledger
                      </span>
                    </div>

                    {totalRefunded > 0 && (
                      <div className="d-flex justify-content-between py-2 border-bottom text-danger">
                        <span className="fw-medium">Total Processed Refunds</span>
                        <span className="fw-bold font-monospace">
                          - Rs. {parseFloat(totalRefunded).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {totalRefunded > 0 && remainingRefundable >= 0 && (
                      <div className="d-flex justify-content-between py-2 border-bottom text-secondary">
                        <span>Remaining Refundable Balance</span>
                        <span className="fw-semibold font-monospace">
                          Rs. {parseFloat(remainingRefundable).toLocaleString()}
                        </span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between pt-3">
                      <span className="fw-bold fs-5 text-dark">Invoice Grand Total</span>
                      <span className="fw-bold fs-5 text-success font-monospace">
                        Rs. {parseFloat(order?.total_amount || totalCost).toLocaleString()}
                      </span>
                    </div>

                    {totalSavings > 0 && (
                      <div className="d-flex justify-content-between pt-1 text-success fw-bold" style={{ fontSize: "12.5px" }}>
                        <span>Total Customer Savings:</span>
                        <span className="font-monospace">Rs. {totalSavings.toLocaleString()} OFF</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Column: Customer & Order Metadata */}
        <div className="col-lg-4">
          {/* Customer Profile Card */}
          <div className="admin-card mb-4">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaUser className="text-primary" /> Customer Profile
              </h5>
            </div>
            <div className="admin-card-body">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  className="rounded-circle bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center fw-bold fs-5"
                  style={{ width: "48px", height: "48px" }}
                >
                  {customerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h6 className="fw-bold text-dark mb-0">{customerName}</h6>
                  <span className="text-muted small">Customer Order</span>
                </div>
              </div>

              <div className="d-flex flex-column gap-2.5 small">
                {order?.email && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <FaEnvelope className="text-primary flex-shrink-0" />
                    <span className="text-truncate">{order.email}</span>
                  </div>
                )}
                {order?.phone && (
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <FaPhone className="text-success flex-shrink-0" />
                    <span>{order.phone}</span>
                  </div>
                )}
                {order?.address && (
                  <div className="d-flex align-items-start gap-2 text-muted mt-1">
                    <FaMapMarkerAlt className="text-danger flex-shrink-0 mt-1" />
                    <span>
                      {order.address}, {order.city} {order.state && `, ${order.state}`} {order.zipcode}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sales Transaction Card */}
          <div className="admin-card">
            <div className="admin-card-header bg-white">
              <h5 className="admin-card-title d-flex align-items-center gap-2">
                <FaShieldAlt className="text-primary" /> Sales Transaction Metadata
              </h5>
            </div>
            <div className="admin-card-body small">
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Tracking No</span>
                <span className="fw-bold text-dark font-monospace">
                  {order?.tracking_no || "—"}
                </span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Order ID</span>
                <span className="fw-semibold text-dark">#{orderId}</span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Payment Mode</span>
                <span className="fw-semibold text-dark">
                  {order?.payment_mode || "COD"}
                </span>
              </div>
              <div className="d-flex justify-content-between py-1.5 border-bottom">
                <span className="text-muted">Completed On</span>
                <span className="text-dark">
                  {getValidMoment(order?.order_complete, order?.created_at)?.format("DD MMM YYYY, hh:mm A") || "—"}
                </span>
              </div>
              <div className="d-flex justify-content-between pt-2">
                <span className="text-muted">Fulfilled By</span>
                <span className="fw-semibold text-dark">
                  {order?.remark || "System Admin"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
